import { Search } from "@/components/search/Search.tsx";
import { SidebarRecruitmentJobItem } from "@/components/sidebar/recruit/components/SidebarRecruitmentJobItem.tsx";
import {
  useEmployeeJobsPinsSetting,
  useEmployeeJobsPinsSettingSave,
} from "@/queries/use-employee-settings.query.ts";
import { useEmployeeAppConfigStore } from "@/stores/employee.store.ts";
import {
  parseUrlHelper,
  Avatar,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  ListItem,
  Page,
  SidebarCollapseButton,
  Skeleton,
  Stack,
  Text,
} from "@skailer-com/uikit";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import {
  useApplicantsMentions,
  useApplicantsSearchInfinite,
} from "@/queries/recruit/use-applicants.query.ts";
import { useHiringPlanSearch } from "@/queries/recruit/use-hiring-plan.query.ts";
import {
  useJobDetails,
  useJobsByIds,
  useJobsSearch,
  useJobPatchPriority,
  jobsKeys,
} from "@/queries/recruit/use-jobs.query.ts";
import { ApplicantFilterDefault } from "@/types/recruit/applicant.types.ts";
import { ApiSorting, ApiSortingDirection } from "@/types/api.types.ts";
import {
  ApiJob,
  ApiJobsFilter,
  ApiJobsFilterDefault,
  isJobActive,
  JobPriority,
  JobSorting,
  JobStatus,
} from "@/types/recruit/job.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDebounce } from "react-use";
import { twMerge } from "tailwind-merge";

const getJobPriorityWeight = (priority: JobPriority | null) => {
  switch (priority) {
    case JobPriority.high:
      return 3;
    case JobPriority.medium:
      return 2;
    case JobPriority.low:
      return 1;
    default:
      return 0;
  }
};

const compareByCreatedAtDesc = (left: ApiJob, right: ApiJob) => {
  return (
    new Date(right.datetime_created_at).getTime() - new Date(left.datetime_created_at).getTime()
  );
};

const insertSorted = (
  items: ApiJob[],
  item: ApiJob,
  comparator: (left: ApiJob, right: ApiJob) => number
) => {
  let insertIndex = items.length;
  for (let i = 0; i < items.length; i++) {
    if (comparator(item, items[i]) < 0) {
      insertIndex = i;
      break;
    }
  }
  items.splice(insertIndex, 0, item);
};

const buildCachedJobsById = (
  queryClient: ReturnType<typeof useQueryClient>,
  pinnedJobIds: number[]
) => {
  const pinnedJobIdsSet = new Set(pinnedJobIds);
  const cachedJobsById = new Map<number, ApiJob>();
  const cachedJobsSearchData = queryClient.getQueriesData<{ items: ApiJob[] }>({
    queryKey: jobsKeys.search._def,
  });

  cachedJobsSearchData.forEach(([, data]) => {
    data?.items?.forEach((jobItem) => {
      if (pinnedJobIdsSet.has(jobItem.id) && !cachedJobsById.has(jobItem.id)) {
        cachedJobsById.set(jobItem.id, jobItem);
      }
    });
  });

  pinnedJobIds.forEach((id) => {
    const detailsData = queryClient.getQueryData<ApiJob>(jobsKeys.details(id).queryKey);
    if (detailsData && !cachedJobsById.has(id)) {
      cachedJobsById.set(id, detailsData);
    }
  });

  return cachedJobsById;
};

const mergeJobsWithPinned = ({
  jobsItems,
  pinnedJobIds,
  pinnedJobs,
  queryClient,
  sorting,
}: {
  jobsItems: ApiJob[];
  pinnedJobIds: number[];
  pinnedJobs: ApiJob[] | undefined;
  queryClient: ReturnType<typeof useQueryClient>;
  sorting: ApiSorting;
}) => {
  const pinnedJobsById = new Map((pinnedJobs || []).map((jobItem) => [jobItem.id, jobItem]));
  const cachedJobsById = buildCachedJobsById(queryClient, pinnedJobIds);
  const jobsById = new Map(jobsItems.map((jobItem) => [jobItem.id, jobItem]));
  const additionalPinnedJobs = pinnedJobIds
    .map((id) => pinnedJobsById.get(id) || cachedJobsById.get(id))
    .filter((jobItem): jobItem is ApiJob => !!jobItem && !jobsById.has(jobItem.id));

  const sortField = sorting.fields[0]?.field;
  const sortDirection = sorting.fields[0]?.direction;

  if (sortField === JobSorting.touchpoint) {
    const orderedPinnedJobs = pinnedJobIds
      .map((id) => jobsById.get(id) || pinnedJobsById.get(id) || cachedJobsById.get(id))
      .filter((jobItem): jobItem is ApiJob => !!jobItem);
    const pinnedIdsSet = new Set(orderedPinnedJobs.map((jobItem) => jobItem.id));
    const regularJobs = jobsItems.filter((jobItem) => !pinnedIdsSet.has(jobItem.id));
    return [...orderedPinnedJobs, ...regularJobs];
  }

  if (sortField === JobSorting.priority) {
    const comparator = (left: ApiJob, right: ApiJob) => {
      const priorityDiff =
        getJobPriorityWeight(right.priority) - getJobPriorityWeight(left.priority);
      if (priorityDiff !== 0) {
        return sortDirection === ApiSortingDirection.desc ? priorityDiff : -priorityDiff;
      }
      return compareByCreatedAtDesc(left, right);
    };

    const mergedJobs = [...jobsItems];
    additionalPinnedJobs.forEach((jobItem) => {
      insertSorted(mergedJobs, jobItem, comparator);
    });
    return mergedJobs;
  }

  if (sortField === JobSorting.title) {
    const comparator = (left: ApiJob, right: ApiJob) => {
      const titleDiff = left.title.localeCompare(right.title);
      if (titleDiff !== 0) {
        return sortDirection === ApiSortingDirection.asc ? titleDiff : -titleDiff;
      }
      return compareByCreatedAtDesc(left, right);
    };

    const mergedJobs = [...jobsItems];
    additionalPinnedJobs.forEach((jobItem) => {
      insertSorted(mergedJobs, jobItem, comparator);
    });
    return mergedJobs;
  }

  return [...jobsItems, ...additionalPinnedJobs];
};

export const SidebarRecruitmentIndex = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const defaultJobFilter: ApiJobsFilter = {
    ...ApiJobsFilterDefault,
    status_list: [JobStatus.published],
    my_jobs: true,
  };

  const { data: totalApplicantsCount, isLoading: totalApplicantsIsLoading } =
    useApplicantsSearchInfinite(ApplicantFilterDefault);
  const totalApplicantsCountValue = totalApplicantsCount?.pages[0]?.metadata.total_items || 0;

  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<ApiJobsFilter>(defaultJobFilter);
  const { recruitSidebarJobSorting: sidebarSorting, setRecruitSidebarJobSorting } =
    useEmployeeAppConfigStore();
  const { pathname } = useLocation();
  const jobId = parseUrlHelper.getNumberArray(searchParams, "job_id");
  const mentionState = searchParams.get("mention_state");
  const isSidebarJobsFilterAvailable = pathname.startsWith("/recruitment");
  const showPriorityStripe =
    isSidebarJobsFilterAvailable && sidebarSorting.fields[0]?.field === JobSorting.priority;
  const jobsFilter = useMemo(() => {
    return {
      ...filter,
      sorting: isSidebarJobsFilterAvailable ? sidebarSorting : undefined,
    };
  }, [filter, isSidebarJobsFilterAvailable, sidebarSorting]);
  const { data: jobs, isLoading: jobsIsLoading } = useJobsSearch(jobsFilter);
  const { data: jobsPinsSetting } = useEmployeeJobsPinsSetting();
  const jobsPinsSettingSave = useEmployeeJobsPinsSettingSave();
  const pinnedJobIds = jobsPinsSetting?.job_ids || [];
  const shouldIncludePinnedJobsInList = isSidebarJobsFilterAvailable;
  const { data: pinnedJobs } = useJobsByIds({
    ids: pinnedJobIds,
    enabled: shouldIncludePinnedJobsInList,
    includeCandidatesCount: true,
  });
  const jobPatchPriority = useJobPatchPriority();
  const { data: mentions, isLoading: mentionsIsLoading } = useApplicantsMentions();
  const jobDetails = useJobDetails(jobId && jobId.length == 1 ? jobId[0] : undefined);
  const { canIView, canIEdit, isFeatureEnabled } = usePermissions();
  const { data: hiringPlan, isLoading: hiringPlanIsLoading } = useHiringPlanSearch(
    filter,
    canIView(ApiPermission.manage_hiring_plan) && isFeatureEnabled(ApiFeatureType.ats_hiring_plan)
  );

  const activeClasses = "bg-light border-r";
  const listItemBaseClasses = "py-1 px-4 hover:bg-light hover:border-r";
  const [searchTerm, setSearchTerm] = useState(filter.text);
  const [openedJobMenuId, setOpenedJobMenuId] = useState<number | null>(null);
  const [isSortingMenuOpen, setIsSortingMenuOpen] = useState(false);
  const canEditJob = canIEdit(ApiPermission.manage_jobs);
  const pinnedJobIdsSet = useMemo(() => new Set(pinnedJobIds), [pinnedJobIds]);

  const handleEditJob = (jobItem: ApiJob) => {
    if (!canEditJob) {
      return;
    }

    sessionStorage.setItem("job_edit_referrer", pathname + window.location.search);
    navigate(`/recruitment/jobs/${jobItem.id}/edit/`);
  };

  const handleJobPriorityChange = (jobItem: ApiJob, priority: JobPriority | null) => {
    if (!canEditJob) {
      return;
    }

    jobPatchPriority.mutate({ id: jobItem.id, priority });
  };

  const handleToggleJobPin = (jobItem: ApiJob) => {
    if (!isJobActive(jobItem.status)) {
      return;
    }

    const nextPinnedJobIds = pinnedJobIdsSet.has(jobItem.id)
      ? pinnedJobIds.filter((id) => id !== jobItem.id)
      : [...pinnedJobIds, jobItem.id];

    jobsPinsSettingSave.mutate({ job_ids: nextPinnedJobIds });
  };

  const handleJobMenuOpenChange = (jobId: number, open: boolean) => {
    setOpenedJobMenuId((currentOpenedId) => {
      if (open) {
        return jobId;
      }

      return currentOpenedId === jobId ? null : currentOpenedId;
    });
  };

  useDebounce(
    () => {
      const normalizedSearchTerm = searchTerm?.trim() ?? "";
      const hasSearchTerm = normalizedSearchTerm.length > 0;

      setFilter((prev) => ({
        ...prev,
        text: hasSearchTerm ? normalizedSearchTerm : undefined,
        page_number: 1,
        my_jobs: hasSearchTerm ? false : defaultJobFilter.my_jobs,
      }));
    },
    300,
    [searchTerm]
  );

  const sidebarJobs = useMemo(() => {
    if (!jobs) {
      return [];
    }

    if (!shouldIncludePinnedJobsInList || pinnedJobIds.length === 0) {
      return jobs.items;
    }

    return mergeJobsWithPinned({
      jobsItems: jobs.items,
      pinnedJobIds,
      pinnedJobs,
      queryClient,
      sorting: sidebarSorting,
    });
  }, [jobs, pinnedJobs, pinnedJobIds, shouldIncludePinnedJobsInList, sidebarSorting, queryClient]);

  return (
    <>
      <Stack direction={"horizontal"} items={"center"} justify={"between"} className={"pr-6"}>
        <Page.Header title={t("ats:recruit")} />
        <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
          {isSidebarJobsFilterAvailable && (
            <DropdownMenu
              open={isSortingMenuOpen}
              onOpenChange={setIsSortingMenuOpen}
              trigger={
                <Button
                  variant={"secondary"}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-bars-filter"} />}
                ></Button>
              }
            >
              <DropdownMenuItem
                onClick={() => {
                  setRecruitSidebarJobSorting({
                    fields: [{ field: JobSorting.touchpoint, direction: ApiSortingDirection.desc }],
                  });
                }}
                title={t("ats:sorting_most_recent")}
                isSelected={sidebarSorting.fields[0]?.field === JobSorting.touchpoint}
              />
              <DropdownMenuItem
                onClick={() => {
                  setRecruitSidebarJobSorting({
                    fields: [{ field: JobSorting.title, direction: ApiSortingDirection.asc }],
                  });
                }}
                title={t("ats:sorting_a_z")}
                isSelected={sidebarSorting.fields[0]?.field === JobSorting.title}
              />
              <DropdownMenuItem
                onClick={() => {
                  setRecruitSidebarJobSorting({
                    fields: [{ field: JobSorting.priority, direction: ApiSortingDirection.desc }],
                  });
                }}
                title={t("ats:sorting_by_priority")}
                isSelected={sidebarSorting.fields[0]?.field === JobSorting.priority}
              />
            </DropdownMenu>
          )}
          <SidebarCollapseButton variant={"secondary"} />
        </Stack>
      </Stack>
      <Search
        className={"w-full"}
        innerPadding={true}
        borderless={true}
        placeholder={t("ats:job")}
        value={searchTerm}
        onInput={(e) => {
          setSearchTerm(e.currentTarget.value);
        }}
        clearable={true}
        onClear={() => {
          setSearchTerm("");
          setFilter(defaultJobFilter);
        }}
      />
      <Stack>
        <ListItem
          title={t("ats:candidates_all")}
          caption={
            totalApplicantsIsLoading
              ? "..."
              : t("plurals:plural_candidates", { count: totalApplicantsCountValue })
          }
          icon={<Avatar icon={<FontAwesomeIcon icon={"fa-light fa-grid-2"} />} size={"md"} />}
          className={twMerge(
            listItemBaseClasses,
            "border-t",
            jobId.length == 0 && !mentionState && pathname.startsWith("/recruitment/candidates")
              ? activeClasses
              : ""
          )}
          to={"/recruitment/candidates"}
        />

        <ListItem
          title={t("ats:all_jobs")}
          caption={""}
          icon={<Avatar icon={<FontAwesomeIcon icon={"fa-light fa-ellipsis"} />} size={"md"} />}
          className={twMerge(
            listItemBaseClasses,
            jobId.length == 0 && pathname.startsWith("/recruitment/jobs") ? activeClasses : ""
          )}
          to={"/recruitment/jobs"}
        />
        {canIView(ApiPermission.manage_hiring_plan) &&
          isFeatureEnabled(ApiFeatureType.ats_hiring_plan) && (
            <ListItem
              title={t("hiring_plan:hiring_plan")}
              captionSlot={
                <Text className={(hiringPlan?.awaiting_count ?? 0) > 0 ? "text-new" : ""}>
                  {hiringPlanIsLoading
                    ? "..."
                    : (hiringPlan?.awaiting_count ?? 0) > 0
                      ? t("hiring_plan:count_pending", { count: hiringPlan?.awaiting_count ?? 0 })
                      : ""}
                </Text>
              }
              icon={
                <Box className={"relative"}>
                  {(hiringPlan?.awaiting_count ?? 0) > 0 && (
                    <Box
                      className={"w-2 h-2 bg-new rounded-full absolute -top-0.5 -right-0.5"}
                    ></Box>
                  )}
                  <Avatar
                    icon={<FontAwesomeIcon icon={"fa-light fa-calendar-lines-pen"} />}
                    size={"md"}
                  />
                </Box>
              }
              className={twMerge(
                listItemBaseClasses,
                jobId.length == 0 && pathname.startsWith("/recruitment/hiring-plan")
                  ? activeClasses
                  : ""
              )}
              to={"/recruitment/hiring-plan"}
            />
          )}
        <ListItem
          title={t("core:mentions")}
          captionSlot={
            <Text
              className={(mentions?.unread_mentions_count ?? 0) > 0 ? "text-new" : "text-secondary"}
            >
              {mentionsIsLoading
                ? "..."
                : t("plurals:new_mentions", { count: mentions?.unread_mentions_count ?? 0 })}
            </Text>
          }
          icon={
            <Box className={"relative"}>
              {(mentions?.unread_mentions_count ?? 0) > 0 && (
                <Box className={"w-2 h-2 bg-new rounded-full absolute -top-0.5 -right-0.5"}></Box>
              )}
              <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-at"} />} size={"md"} />
            </Box>
          }
          className={twMerge(
            listItemBaseClasses,
            jobId.length == 0 && mentionState && pathname.startsWith("/recruitment/candidates")
              ? activeClasses
              : ""
          )}
          to={"/recruitment/candidates?mention_state=any"}
        />

        {jobsIsLoading && <Skeleton className={"w-full h-5"}></Skeleton>}
        {jobs && (
          <>
            {sidebarJobs.map((jobItem) => {
              const isJobMenuOpen = openedJobMenuId === jobItem.id;
              const currentUrlParams = new URLSearchParams(window.location.search);
              currentUrlParams.set("job_id", jobItem.id.toString());
              currentUrlParams.delete("mention_state");
              const url = "/recruitment/candidates?" + currentUrlParams.toString();
              return (
                <SidebarRecruitmentJobItem
                  key={`secondary-menu-job-${jobItem.id}`}
                  jobItem={jobItem}
                  url={url}
                  isActive={jobDetails.data?.id === jobItem.id}
                  showPriorityStripe={showPriorityStripe}
                  showPinIndicator={sidebarSorting.fields[0]?.field !== JobSorting.priority}
                  isPinned={pinnedJobIdsSet.has(jobItem.id)}
                  canPinJob={
                    sidebarSorting.fields[0]?.field !== JobSorting.priority &&
                    isJobActive(jobItem.status)
                  }
                  canEditJob={canEditJob}
                  isMenuOpen={isJobMenuOpen}
                  onMenuOpenChange={handleJobMenuOpenChange}
                  onEditJob={handleEditJob}
                  onTogglePin={handleToggleJobPin}
                  onJobPriorityChange={handleJobPriorityChange}
                />
              );
            })}
          </>
        )}
      </Stack>
    </>
  );
};
